import React, { useLayoutEffect, useState, useSyncExternalStore } from 'react';
import { Box } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DASHBOARD_ACCENT } from '../../utils/dashboardSurfaceSx';
import {
  clearRoomMoveHoverBookingId,
  getRoomMoveHoverBookingId,
  subscribeRoomMoveHover,
} from '../../utils/roomMoveHoverStore';

/**
 * Prefer OTA / PMS source logo; fall back to room-change / upgrade icons.
 */
function resolveAnchorEl(barNode) {
  if (!barNode) return null;
  const anchors = Array.from(barNode.querySelectorAll('[data-room-move-anchor]'));
  if (!anchors.length) return barNode;
  return (
    anchors.find((el) => el.getAttribute('data-room-move-anchor') === 'source')
    || anchors.find((el) => el.getAttribute('data-room-move-anchor') === 'change')
    || anchors.find((el) => el.getAttribute('data-room-move-anchor') === 'upgrade')
    || anchors.find((el) => el.getAttribute('data-room-move-anchor') === 'downgrade')
    || anchors[0]
  );
}

function pointFromEl(el, root, rootRect) {
  const r = el.getBoundingClientRect();
  return {
    x: r.left - rootRect.left + root.scrollLeft + r.width / 2,
    y: r.top - rootRect.top + root.scrollTop + r.height / 2,
  };
}

function resolveScrollParents(root) {
  const parents = [];
  if (!root) return parents;
  let node = root;
  while (node && node !== document.body) {
    if (node.nodeType === 1) {
      const style = window.getComputedStyle(node);
      const overflowY = style.overflowY || style.overflow;
      const overflowX = style.overflowX || style.overflow;
      if (
        /(auto|scroll|overlay)/.test(overflowY)
        || /(auto|scroll|overlay)/.test(overflowX)
      ) {
        parents.push(node);
      }
    }
    node = node.parentElement;
  }
  const boardRoot = document.querySelector('[data-dashboard-room-board-root]');
  if (boardRoot && !parents.includes(boardRoot)) parents.push(boardRoot);
  return parents;
}

/**
 * Draws fancy curved connectors between room-change / upgrade bars for one booking.
 * Endpoints snap to OTA/PMS source logos (`data-room-move-anchor="source"`).
 * Line color matches the PMS / channel logo accent (teal).
 */
export default function RoomMoveLinkOverlay({
  containerRef,
  bookingId: bookingIdProp = null,
  isDark = true,
  accent = DASHBOARD_ACCENT,
}) {
  const hoveredBookingId = useSyncExternalStore(
    subscribeRoomMoveHover,
    getRoomMoveHoverBookingId,
    getRoomMoveHoverBookingId,
  );
  const bookingId = bookingIdProp != null ? bookingIdProp : hoveredBookingId;
  const [geometry, setGeometry] = useState({ width: 0, height: 0, paths: [], dots: [] });

  // While scrolling the board, drop hover/connector so scroll stays smooth (no layout thrash).
  useLayoutEffect(() => {
    const root = containerRef?.current;
    if (!root) return undefined;

    const onScroll = () => {
      if (getRoomMoveHoverBookingId() != null) {
        clearRoomMoveHoverBookingId();
      }
    };

    const scrollParents = resolveScrollParents(root);
    scrollParents.forEach((el) => el.addEventListener('scroll', onScroll, { passive: true }));
    return () => {
      scrollParents.forEach((el) => el.removeEventListener('scroll', onScroll));
    };
  }, [containerRef]);

  useLayoutEffect(() => {
    const root = containerRef?.current;
    if (!root || !bookingId) {
      setGeometry({ width: 0, height: 0, paths: [], dots: [] });
      return undefined;
    }

    let frame = 0;
    let settleTimer = 0;
    const measure = () => {
      const nodes = Array.from(
        root.querySelectorAll(`[data-room-move-booking="${bookingId}"]`),
      );
      const rootRect = root.getBoundingClientRect();
      const width = Math.max(root.scrollWidth, root.clientWidth, 1);
      const height = Math.max(root.scrollHeight, root.clientHeight, 1);

      if (nodes.length < 2) {
        setGeometry({ width, height, paths: [], dots: [] });
        return;
      }

      const points = nodes
        .map((node) => {
          const barRect = node.getBoundingClientRect();
          const left = barRect.left - rootRect.left + root.scrollLeft;
          const top = barRect.top - rootRect.top + root.scrollTop;
          const midY = top + barRect.height / 2;
          const anchor = resolveAnchorEl(node);
          const hasIconAnchor = Boolean(
            anchor && anchor !== node && anchor.hasAttribute?.('data-room-move-anchor'),
          );
          const pt = hasIconAnchor
            ? pointFromEl(anchor, root, rootRect)
            : {
              x: left + Math.min(14, Math.max(8, barRect.width * 0.12)),
              y: midY,
            };
          return {
            ...pt,
            midY,
            left,
            barWidth: barRect.width,
            barHeight: barRect.height,
            hasIconAnchor,
          };
        })
        .filter((p) => p.barWidth > 2 && p.barHeight > 2)
        .sort((a, b) => a.midY - b.midY || a.left - b.left);

      const paths = [];
      for (let i = 0; i < points.length - 1; i += 1) {
        const a = points[i];
        const b = points[i + 1];
        const x1 = a.x;
        const y1 = a.y;
        const x2 = b.x;
        const y2 = b.y;
        const midX = (x1 + x2) / 2;
        const bulge = Math.max(24, Math.min(68, Math.abs(y2 - y1) * 0.32));
        const c1x = midX;
        const c1y = y1 + (y2 >= y1 ? bulge * 0.18 : -bulge * 0.18);
        const c2x = midX;
        const c2y = y2 + (y2 >= y1 ? -bulge * 0.18 : bulge * 0.18);
        const d = `M ${x1} ${y1} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${x2} ${y2}`;
        paths.push({ d, key: `${i}-${Math.round(x1)}-${Math.round(y1)}-${Math.round(x2)}` });
      }

      setGeometry({ width, height, paths, dots: [] });
    };

    const schedule = (withSettle = false) => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        measure();
        if (!withSettle) return;
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(measure, 220);
      });
    };

    const onResize = () => schedule(false);
    schedule(true);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(onResize) : null;
    if (ro) ro.observe(root);
    window.addEventListener('resize', onResize);

    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(settleTimer);
      if (ro) ro.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [containerRef, bookingId]);

  if (!bookingId || geometry.paths.length === 0) return null;

  const lineColor = accent || DASHBOARD_ACCENT;
  const gradId = `room-move-link-${bookingId}`;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: 0,
        width: geometry.width,
        height: geometry.height,
        pointerEvents: 'none',
        zIndex: 24,
        overflow: 'visible',
      }}
    >
      <svg
        width={geometry.width}
        height={geometry.height}
        style={{ overflow: 'visible', display: 'block' }}
      >
        <defs>
          <linearGradient id={`${gradId}-accent`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={lineColor} stopOpacity="0.98" />
            <stop offset="100%" stopColor={alpha(lineColor, 0.55)} />
          </linearGradient>
          <filter id={`${gradId}-glow`} x="-40%" y="-40%" width="180%" height="180%">
            <feGaussianBlur stdDeviation="3.5" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {geometry.paths.map((path) => (
          <g key={path.key} filter={`url(#${gradId}-glow)`}>
            <path
              d={path.d}
              fill="none"
              stroke={alpha(lineColor, isDark ? 0.38 : 0.3)}
              strokeWidth={8}
              strokeLinecap="round"
            />
            <path
              d={path.d}
              fill="none"
              stroke={`url(#${gradId}-accent)`}
              strokeWidth={2.5}
              strokeLinecap="round"
              strokeDasharray="7 6"
              style={{
                animation: 'roomMoveDash 1.1s linear infinite',
              }}
            />
          </g>
        ))}
      </svg>
      <style>
        {`@keyframes roomMoveDash { to { stroke-dashoffset: -26; } }`}
      </style>
    </Box>
  );
}
